import React from "react";
import { Helmet } from "react-helmet-async";

export interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogType?: "website" | "article";
  ogImage?: string;
  twitterImage?: string;
  noindex?: boolean;
  keywords?: string;
  structuredData?: unknown;
}

const DEFAULT_OG_IMAGE = "https://terramatrix.io/og-image.jpg";
const DEFAULT_TWITTER_IMAGE = "https://terramatrix.io/twitter-image.jpg";
const SITE_NAME = "TerraMatrix";
const BASE_URL = "https://terramatrix.io";

const getCanonicalUrl = (canonical: string | undefined): string | undefined => {
  if (!canonical) {
    return undefined;
  }

  try {
    const url = new URL(canonical, BASE_URL);
    return url.origin === BASE_URL ? url.toString() : BASE_URL;
  } catch {
    return BASE_URL;
  }
};

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonical,
  ogType = "website",
  ogImage = DEFAULT_OG_IMAGE,
  twitterImage = DEFAULT_TWITTER_IMAGE,
  noindex = false,
  keywords,
  structuredData,
}) => {
  const fullTitle = title.includes("TerraMatrix")
    ? title
    : `${title} | ${SITE_NAME} - IoT Sensor Analytics`;

  const canonicalUrl = getCanonicalUrl(canonical);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Robots */}
      <meta
        name="robots"
        content={noindex ? "noindex, nofollow" : "index, follow"}
      />

      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph Tags */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@terramatrix" />
      <meta name="twitter:creator" content="@terramatrix" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={twitterImage} />

      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

// Organization structured data for homepage
export const organizationStructuredData = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "TerraMatrix",
  url: "https://terramatrix.io",
  logo: "https://terramatrix.io/logo.png",
  description:
    "IoT sensor systems and computer vision analytics for restaurant and industrial monitoring. Real-time data aggregation, predictive maintenance, and actionable insights.",
  foundingDate: "2023",
  sameAs: [
    "https://twitter.com/terramatrix",
    "https://linkedin.com/company/terramatrix",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "Sales",
    email: "sales@terramatrix.io",
    url: "https://terramatrix.io/contact",
  },
};
